import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { usePostHog } from 'posthog-js/react';
import { useTranslations } from 'use-intl';

import { ADD_FORM_MESSAGE_PREVIEW } from '@/manager-graphql';
import {
  captureResponseStartedOnce,
  useIsFunnelTracked,
} from '@/shared-measurement';
import {
  ADD_FORM_MESSAGE,
  type MessageAssetInput,
  type SubQuestionAnswerInput,
} from '@/survey-graphql';
import { toast } from '@/ui';

import type { AIFormQuestion } from './survey-ai-forms-form-section.types';
import { getNextUnsubmittedQuestionInSequence } from './survey-ai-forms.helpers';

interface UseSurveyAiFormsSubmissionMessageProps {
  projectId: string;
  sessionId: string;
  isPreviewMode: boolean;
  questions: AIFormQuestion[];
  currentQuestionId: string | null;
  setCurrentQuestionId: (id: string | null) => void;
}

export function useSurveyAiFormsSubmissionMessage({
  projectId,
  isPreviewMode,
  questions,
  currentQuestionId,
  setCurrentQuestionId,
}: UseSurveyAiFormsSubmissionMessageProps) {
  const t = useTranslations('AIForms.toast');
  const posthog = usePostHog();
  const isFunnelTracked = useIsFunnelTracked();
  const [submittedQuestionIds, setSubmittedQuestionIds] = useState<Set<string>>(
    new Set()
  );

  const [addFormMessage, { loading: isAddingMessage }] =
    useMutation(ADD_FORM_MESSAGE);
  const [addFormMessagePreview, { loading: isAddingMessagePreview }] =
    useMutation(ADD_FORM_MESSAGE_PREVIEW);

  const isAddingMessageLoading = isPreviewMode
    ? isAddingMessagePreview
    : isAddingMessage;

  const handleAddFormMessage = async (messageData: {
    content: string;
    insightId: string | null;
    questionId: string;
    sessionId: string;
    transcript?: string;
    assets?: MessageAssetInput[];
    subQuestionAnswers?: SubQuestionAnswerInput[];
  }): Promise<Set<string> | null> => {
    const subQuestionAnswers =
      messageData.subQuestionAnswers &&
      messageData.subQuestionAnswers.length > 0
        ? messageData.subQuestionAnswers
        : undefined;
    try {
      if (isPreviewMode) {
        await addFormMessagePreview({
          variables: {
            input: {
              content: messageData.content,
              projectId: projectId,
              questionId: messageData.questionId,
              sessionId: messageData.sessionId,
              transcript: messageData.transcript,
              assets: messageData.assets,
              subQuestionAnswers,
            },
          },
        });
      } else {
        if (!messageData.insightId) {
          throw new Error('insightId is required when not in preview mode');
        }
        await addFormMessage({
          variables: {
            input: {
              content: messageData.content,
              insightId: messageData.insightId,
              questionId: messageData.questionId,
              sessionId: messageData.sessionId,
              transcript: messageData.transcript,
              assets: messageData.assets,
              subQuestionAnswers,
            },
          },
        });
      }

      if (!isPreviewMode && isFunnelTracked) {
        captureResponseStartedOnce(posthog, {
          projectId,
          responseId: messageData.insightId,
        });
      }

      const updatedSet = new Set(submittedQuestionIds).add(
        messageData.questionId
      );

      setSubmittedQuestionIds(updatedSet);

      return updatedSet;
    } catch (error: unknown) {
      toast.add({
        title: error instanceof Error ? error.message : t('genericError'),
        type: 'error',
      });
      return null;
    }
  };

  const moveToNextQuestion = (updatedSubmittedIds: Set<string>) => {
    const nextQuestion = getNextUnsubmittedQuestionInSequence(
      questions,
      updatedSubmittedIds,
      currentQuestionId
    );
    if (nextQuestion) {
      setCurrentQuestionId(nextQuestion.id);
    } else {
      setCurrentQuestionId(null);
    }
  };

  return {
    submittedQuestionIds,
    setSubmittedQuestionIds,
    isAddingMessage: isAddingMessageLoading,
    handleAddFormMessage,
    moveToNextQuestion,
  };
}
